import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import SupervisorNav from '../../components/supervisor/SupervisorNav'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import EmptyState from '../../components/shell/EmptyState'
import StatusChip from '../../components/agent/StatusChip'
import { countsAsAvailable } from '../../components/agent/scheduleDay'
import TeamSwitcher from '../../components/supervisor/TeamSwitcher'
import { supabase } from '../../lib/supabase'
import { useRefetchOnFocus } from '../../lib/useRefetchOnFocus'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { PAY_PERIOD, PAY_PERIOD_DATES } from '../agent/payPeriod19'
import { PAY_PERIOD_20_DATES } from '../agent/payPeriod20'
import { getTeamLeaveRequests, type TeamLeaveRequest } from './leaveRequestQueue'
import { getTeamScheduleSubmissions, type TeamScheduleSubmission } from './scheduleSubmissionQueue'
import { getTeamAvailabilityExceptions, type TeamAvailabilityException } from './availabilityExceptionQueue'
import { getTeamOpenShiftClaims, type TeamOpenShiftClaim } from './openShiftClaimQueue'
import { getTeamShiftSwaps, type TeamShiftSwap } from './shiftSwapQueue'
import { useSupervisorTeams } from './useSupervisorTeams'
import InsightsSummary from './InsightsSummary'
import { getBoardRoster, type BoardRosterRow } from './boardRosterStore'
import { overridesForDay } from './planningBoardDraftStore'
import { coverageFor } from './coverageAnalysis'
import {
  getQualificationsOn,
  getRecentPublications,
  getTeamOvertime,
  type OvertimeEntry,
  type PublishedItem,
} from './supervisorDashboardStore'
import { remindUnacknowledged } from './scheduleReminderStore'
import { schedulingServiceEnabled } from '../../lib/features'
import { getStaffingTargets, getTargetCatalog, resolveShiftTarget, resolveTarget, weekdayNameFor, type StaffingTarget, type TargetCatalog } from './staffingTargetStore'
import { getPersonnelDetails, type PersonnelDetail } from './personnelDetailStore'

/**
 * The team toggle (TeamSwitcher.tsx) lists the teams the signed-in
 * supervisor owns (docs/product/personas.md's "A supervisor owns one or
 * more teams") and switches which one the manpower tiles, coverage gaps,
 * detailed-out list, planned overtime, and recent publications describe,
 * and which team's own leave requests and schedule submissions "Needs
 * your attention" shows. Every one reads real records: today's
 * schedule_entries for the team's employees (boardRosterStore.ts) with an
 * approved leave request applied over them, planned overtime and
 * publications from their own tables (supervisorDashboardStore.ts), and
 * the staffing targets the supervisor has configured
 * (staffingTargetStore.ts) measured against those same entries.
 */

// "Sep 18" from a fixture day's own "September" and "18": every short date
// label on this page (the coverage panel's title, the overtime and
// published-history entries, the recurring-pattern signal's own Fridays)
// reads a fixture day this same way, rather than a hand-typed string.
function shortLabel(day: { month: string; day: number }): string {
  return `${day.month.slice(0, 3)} ${day.day}`
}

// The actual current date, not a fixed date the placeholder pilot data is
// anchored to, the same as AgentCalendarPage.tsx's and
// SupervisorDailyAssignmentsPage.tsx's own REAL_TODAY_ISO. Falls back to
// the current period's own first day, which can no longer actually happen
// now that PAY_PERIOD_DATES (payPeriod19.ts) is generated to always contain today.
const now = new Date()
const REAL_TODAY_ISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
const TODAY_INDEX = Math.max(
  0,
  PAY_PERIOD_DATES.findIndex((day) => day.date === REAL_TODAY_ISO),
)
// "Sep 18" from "September" and "18": the Coverage gaps panel's own title
// names the date this way, matching the short "Sep 18 · 0600–1400" shape
// this pilot's earlier placeholder data used.
const TODAY_SHORT_LABEL = shortLabel(PAY_PERIOD_DATES[TODAY_INDEX])
// The date the dashboard actually shows a roster for, once TODAY_INDEX's
// own fallback has applied: a staffing target lookup has to match this
// date, not REAL_TODAY_ISO, or a target that correctly applies on
// SupervisorDailyAssignmentsPage.tsx's own fallback day would silently
// not apply here on the fallback day this can no longer actually reach.
const SHOWN_TODAY_ISO = PAY_PERIOD_DATES[TODAY_INDEX].date

type QueueItem =
  | { kind: 'submission'; id: string; employee: string; title: string; detail: string; teamId: string }
  | { kind: 'leave'; id: string; employee: string; title: string; detail: string; teamId: string }

// Real leave requests and schedule submissions still awaiting a decision
// (getTeamLeaveRequests, getTeamScheduleSubmissions); a decided one's own
// state already says so, so it drops out here without a separate lookup.
// Each carries the employee's real team, so the team toggle filters to one
// team's own queue.
function buildQueue(leaveRequests: TeamLeaveRequest[], scheduleSubmissions: TeamScheduleSubmission[]): QueueItem[] {
  return [
    ...scheduleSubmissions
      .filter((item) => item.state === 'submitted')
      .map(
        (item): QueueItem => ({
          kind: 'submission',
          id: item.id,
          employee: item.employeeName,
          title: `Schedule submitted · ${item.employeeName}`,
          detail: item.periodLabel,
          teamId: item.teamId,
        }),
      ),
    ...leaveRequests
      .filter((item) => item.state === 'pending')
      .map(
        (item): QueueItem => ({
          kind: 'leave',
          id: item.id,
          employee: item.employeeName,
          title: `Leave request · ${item.employeeName}`,
          detail: `${item.leaveType}, ${item.dateRange}`,
          teamId: item.teamId,
        }),
      ),
  ]
}

function CalendarPlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18l6-6-6-6" />
    </svg>
  )
}

function FlagIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 3v18" />
      <path d="M4 4h12l-2 4 2 4H4" />
    </svg>
  )
}

const QUEUE_META = {
  submission: {
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 2 11 13M22 2 15 22l-4-9-9-4Z" />
      </svg>
    ),
    iconClass: 'bg-info/10 text-info',
  },
  leave: { icon: <CalendarPlusIcon />, iconClass: 'bg-info/10 text-info' },
} as const

function SectionTitle({ children }: { children: string }) {
  return <span className="text-body font-extrabold">{children}</span>
}

function Panel({
  title,
  trailing,
  children,
}: {
  title: string
  trailing?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="rounded-surface border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <span className="text-small font-extrabold">{title}</span>
        {trailing}
      </div>
      {children}
    </div>
  )
}

function PanelRow({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="border-b border-line px-4 py-2.5 last:border-b-0">
      <p className="text-small font-bold">{title}</p>
      <p className="mt-0.5 text-caption text-ink-muted">{detail}</p>
    </div>
  )
}

export default function SupervisorDashboardPage() {
  const identity = useProfileIdentity()
  const { format } = useTimeFormat()
  const [userId, setUserId] = useState<string | null>(null)
  const { teams, currentTeamId, switchTeam } = useSupervisorTeams(userId)
  const [targets, setTargets] = useState<StaffingTarget[]>([])
  const [leaveRequests, setLeaveRequests] = useState<TeamLeaveRequest[]>([])
  const [scheduleSubmissions, setScheduleSubmissions] = useState<TeamScheduleSubmission[]>([])
  // The rest of the approval inbox, counted in the summary card.
  const [availabilityExceptions, setAvailabilityExceptions] = useState<TeamAvailabilityException[]>([])
  const [openShiftClaims, setOpenShiftClaims] = useState<TeamOpenShiftClaim[]>([])
  const [shiftSwaps, setShiftSwaps] = useState<TeamShiftSwap[]>([])
  // The selected team's own real records for the current pay period: every
  // employee's schedule, planned overtime, recent publications, and each
  // employee's qualifications today. Reloaded whenever the team changes.
  const [roster, setRoster] = useState<BoardRosterRow[]>([])
  const [overtime, setOvertime] = useState<OvertimeEntry[]>([])
  const [published, setPublished] = useState<PublishedItem[]>([])
  // What the last reminder for each published version said, by its period and version.
  const [reminderNotes, setReminderNotes] = useState<Record<string, string>>({})
  const [reminding, setReminding] = useState<Set<string>>(new Set())
  const [qualifications, setQualifications] = useState<Map<string, Set<string>>>(new Map())
  const [details, setDetails] = useState<PersonnelDetail[]>([])
  // The team's own shift and assignments: the shift a coverage target is
  // matched against, and the time the Coverage gaps panel names.
  const [catalog, setCatalog] = useState<TargetCatalog>({ shift: null, assignments: [], qualifications: [] })

  // Reloads the real leave requests and schedule submissions a decision
  // could land on from elsewhere: another supervisor tab, another device,
  // or the agent's own resubmission. Nothing here subscribes to realtime
  // changes, so a tab left open on this page would otherwise keep showing
  // exactly what it fetched at mount, the same gap AgentDashboardPage.tsx's
  // own loadRealData closes for an agent's requests.
  async function loadQueues() {
    setLeaveRequests(await getTeamLeaveRequests())
    setScheduleSubmissions(await getTeamScheduleSubmissions())
    // These three belong to the scheduling service, as in the approval inbox.
    if (!schedulingServiceEnabled()) return
    setAvailabilityExceptions(await getTeamAvailabilityExceptions())
    setOpenShiftClaims(await getTeamOpenShiftClaims())
    setShiftSwaps(await getTeamShiftSwaps())
  }

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return
      const id = session?.user.id
      if (id) {
        setUserId(id)
        await loadQueues()
        setTargets(await getStaffingTargets())
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!currentTeamId) return
    let cancelled = false
    Promise.all([
      getBoardRoster(currentTeamId, PAY_PERIOD_DATES, PAY_PERIOD_20_DATES),
      getTeamOvertime(currentTeamId, PAY_PERIOD_DATES[0].date, PAY_PERIOD_DATES[PAY_PERIOD_DATES.length - 1].date),
      getRecentPublications(currentTeamId),
      getQualificationsOn(SHOWN_TODAY_ISO),
      getPersonnelDetails(),
      getTargetCatalog(currentTeamId),
    ]).then(([loadedRoster, loadedOvertime, loadedPublished, loadedQualifications, loadedDetails, loadedCatalog]) => {
      if (cancelled) return
      setRoster(loadedRoster.current)
      setOvertime(loadedOvertime)
      setPublished(loadedPublished)
      setQualifications(loadedQualifications)
      setDetails(loadedDetails)
      setCatalog(loadedCatalog)
    })
    return () => {
      cancelled = true
    }
  }, [currentTeamId])

  async function remind(item: PublishedItem) {
    const key = `${item.version.periodStart}|${item.version.version}`
    // A second press while the first is in flight would ask again.
    if (reminding.has(key)) return
    setReminding((current) => new Set(current).add(key))
    const result = await remindUnacknowledged(item.version).finally(() =>
      setReminding((current) => {
        const next = new Set(current)
        next.delete(key)
        return next
      }),
    )
    const note = !result.ok
      ? result.message
      : result.reminded > 0
        ? `Reminded ${result.reminded} ${result.reminded === 1 ? 'agent' : 'agents'}.`
        : 'Everyone waiting was already reminded in the last day.'
    setReminderNotes((current) => ({ ...current, [`${item.version.periodStart}|${item.version.version}`]: note }))
  }

  useRefetchOnFocus(() => {
    loadQueues()
  })

  const teamName = teams.find((team) => team.id === currentTeamId)?.name ?? ''

  // An approved leave request covering the shown date, the same priority
  // ahead of the base schedule SupervisorPlanningBoardPage.tsx's and
  // SupervisorDailyAssignmentsPage.tsx's own entryFor give it: someone
  // whose leave a supervisor has approved is not on duty here either,
  // regardless of what their schedule entry still says for them.
  function approvedLeaveFor(employeeId: string): { label: string } | undefined {
    const match = leaveRequests.find(
      (item) => item.state === 'approved' && item.employeeId === employeeId && item.dates.includes(SHOWN_TODAY_ISO),
    )
    return match ? { label: match.leaveType } : undefined
  }

  // A detail covering the shown date for someone on this team: they are
  // detailed out, not on duty, whatever their schedule entry still says.
  function activeDetailFor(employeeId: string): PersonnelDetail | undefined {
    return details.find(
      (detail) =>
        detail.employeeId === employeeId &&
        detail.originatingTeamId === currentTeamId &&
        detail.startDate <= SHOWN_TODAY_ISO &&
        SHOWN_TODAY_ISO <= detail.endDate,
    )
  }

  // Today's own entry for every member of the current team who has a
  // schedule for the period: an employee who has not submitted one has
  // no entry to count, so they appear in none of the tiles below.
  // Today as this team's planning board draft has it, the same as the daily
  // assignment board opens it: a change made there and not yet published counts
  // here too.
  const plannedToday = userId && currentTeamId ? overridesForDay(userId, currentTeamId, TODAY_INDEX) : new Map()
  const todayEntries = roster.flatMap((member) => {
    const scheduled = member.days[TODAY_INDEX]
    if (!scheduled) return []
    const planned = plannedToday.get(member.id)
    const base = planned ? { ...scheduled, ...planned, state: undefined, countsAsAvailable: undefined } : scheduled
    const appliedLeave = approvedLeaveFor(member.id)
    const appliedDetail = activeDetailFor(member.id)
    return [
      {
        id: member.id,
        name: member.name,
        entry: appliedLeave
          ? { ...base, category: 'leave' as const, label: appliedLeave.label, detail: 'Full day', state: 'approved' as const, countsAsAvailable: false }
          : appliedDetail
            ? { ...base, category: 'detail' as const, label: `${appliedDetail.type} · ${appliedDetail.destination}`, detail: undefined, countsAsAvailable: false }
            : base,
      },
    ]
  })
  // Available is what coverage measures: work, and any status code that counts as
  // available, such as training.
  const availableToday = todayEntries.filter((person) => countsAsAvailable(person.entry))
  const available = availableToday.length
  const onLeave = todayEntries.filter((person) => person.entry.category === 'leave').length

  // Reads the same staffing target a supervisor configures on
  // SupervisorStaffingTargetsPage.tsx, one gap per assignment a target
  // names for this team: the count of people working that assignment today
  // against the target's minimum. A target's own qualification narrows the
  // count to the group's own qualified people, per
  // docs/product/coverage-and-insights.md's "Qualification, when one is
  // required."
  const targetedAssignments = Array.from(
    new Set(targets.filter((target) => target.team === teamName && target.assignment).map((target) => target.assignment as string)),
  ).sort()
  const assignmentGaps = targetedAssignments
    .flatMap((assignment) => {
      const target = resolveTarget(targets, {
        date: SHOWN_TODAY_ISO,
        weekday: weekdayNameFor(SHOWN_TODAY_ISO),
        shift: catalog.shift?.name ?? '',
        team: teamName,
        assignment,
      })
      if (!target) return []
      const group = todayEntries.filter((person) => person.entry.category === 'work' && person.entry.detail === assignment)
      const qualification = target.qualification
      const actual = qualification
        ? group.filter((person) => qualifications.get(person.id)?.has(qualification) ?? false).length
        : group.length
      return [{ assignment, actual, required: target.minimumHeadcount }]
    })
    .filter((gap) => gap.actual < gap.required)

  // A target naming no assignment is a minimum for the whole shift: everyone
  // available today against it (work, and any status code that counts as
  // available, such as training), whatever their assignment (a target's own
  // qualification narrows that to the qualified), so a shift short overall is
  // a gap even when every named assignment is filled.
  const shiftTarget = resolveShiftTarget(targets, {
    date: SHOWN_TODAY_ISO,
    weekday: weekdayNameFor(SHOWN_TODAY_ISO),
    shift: catalog.shift?.name ?? '',
    team: teamName,
  })
  const shiftActual = shiftTarget?.qualification
    ? availableToday.filter((person) => qualifications.get(person.id)?.has(shiftTarget.qualification as string) ?? false).length
    : availableToday.length
  const shiftGaps =
    shiftTarget && shiftActual < shiftTarget.minimumHeadcount
      ? [{ assignment: 'Whole shift', actual: shiftActual, required: shiftTarget.minimumHeadcount }]
      : []
  const coverageGaps = [...shiftGaps, ...assignmentGaps]

  // Assignments, and the whole shift, with more people than their target plus
  // the overstaffed margin today.
  const overstaffedToday = coverageFor(
    todayEntries.map((person) => ({ id: person.id, category: person.entry.category, detail: person.entry.detail, countsAsAvailable: person.entry.countsAsAvailable })),
    targets,
    { date: SHOWN_TODAY_ISO, team: teamName, shift: catalog.shift?.name ?? '' },
    qualifications,
  ).filter((finding) => finding.state === 'overstaffed')

  // Everyone on a detail today (the details table), then anyone whose own
  // schedule entry is a detail status without one on record.
  const detailedIds = new Set<string>()
  const detailedOut: { title: string; detail: string }[] = []
  for (const detail of details) {
    if (
      detail.originatingTeamId !== currentTeamId ||
      detail.startDate > SHOWN_TODAY_ISO ||
      SHOWN_TODAY_ISO > detail.endDate ||
      detailedIds.has(detail.employeeId)
    ) {
      continue
    }
    detailedIds.add(detail.employeeId)
    detailedOut.push({ title: detail.personName, detail: `${detail.type} · ${detail.destination}` })
  }
  for (const person of todayEntries) {
    if (person.entry.category === 'detail' && !detailedIds.has(person.id)) {
      detailedOut.push({ title: person.name, detail: person.entry.label })
    }
  }

  const overtimeTotal = overtime.reduce((sum, entry) => sum + entry.hours, 0)
  const overtimeEntries = overtime.map((entry) => ({
    title: entry.name,
    detail: `${entry.hours} hrs, ${new Date(`${entry.date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
  }))

  const visibleQueue = buildQueue(leaveRequests, scheduleSubmissions).filter((item) => item.teamId === currentTeamId)

  // Everything the approval inbox is waiting on for this team.
  const awaitingDecision =
    visibleQueue.length +
    availabilityExceptions.filter((item) => item.teamId === currentTeamId && item.state === 'pending').length +
    openShiftClaims.filter((item) => item.teamId === currentTeamId && item.state === 'pending').length +
    shiftSwaps.filter((item) => item.teamId === currentTeamId && item.state === 'accepted').length

  const manpower = [
    { label: 'Available', value: available, to: '/supervisor/daily-assignments' },
    { label: 'On leave', value: onLeave, to: '/supervisor/reports/leave-availability' },
    { label: 'Detailed out', value: detailedOut.length, to: '/supervisor/reports/detailed-out' },
    { label: 'Understaffed shifts', value: coverageGaps.length, to: '/supervisor/daily-assignments', warn: coverageGaps.length > 0 },
  ]

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#supervisor-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="supervisor-main" tabIndex={0} className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <SupervisorNav />
        <div className="mx-auto flex max-w-[1400px] flex-col gap-5 lg:gap-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-h2 font-extrabold">Supervisor Dashboard</h1>
              <p className="mt-1 text-small text-ink-muted">
                {teamName && <>{teamName} &middot; Orlando Field Office &middot; </>}
                {PAY_PERIOD.label}, {PAY_PERIOD.range}
              </p>
            </div>
          </div>

          <TeamSwitcher teams={teams} currentTeamId={currentTeamId} onChange={switchTeam} />

          <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-[1.6fr_1fr] lg:gap-6">
          <InsightsSummary
            short={coverageGaps.map((gap) => `${gap.assignment} (${gap.actual} of ${gap.required})`)}
            over={overstaffedToday.map((finding) => `${finding.assignment} (${finding.actual} of ${finding.required})`)}
            pending={awaitingDecision}
            overtimeHours={overtimeTotal}
          />

          <div className="grid auto-rows-fr grid-cols-2 gap-4">
            {manpower.map((tile) => (
              <Link
                key={tile.label}
                to={tile.to}
                className={`block rounded-surface border bg-canvas p-4 hover:bg-surface-2 ${tile.warn ? 'border-error/30' : 'border-line'}`}
              >
                <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{tile.label}</p>
                <p
                  className={`mt-1 text-h1 font-extrabold [font-variant-numeric:tabular-nums] ${tile.warn ? 'text-error' : ''}`}
                >
                  {tile.value}
                </p>
              </Link>
            ))}
          </div>
          </div>

          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[1.6fr_1fr] lg:gap-6">
            <div className="rounded-surface border border-line bg-canvas">
              <div className="flex items-center justify-between border-b border-line px-4 py-3">
                <SectionTitle>Needs your attention</SectionTitle>
                <Link
                  to="/supervisor/approval-inbox"
                  aria-label="Open the approval inbox"
                  className="flex size-11 flex-none items-center justify-center rounded-control text-ink-muted hover:bg-surface-2"
                >
                  <ChevronRightIcon />
                </Link>
              </div>
              {visibleQueue.length === 0 ? (
                <EmptyState>
                  Nothing needs your attention. Submissions and requests appear here as they come in.
                </EmptyState>
              ) : (
                <ul>
                  {visibleQueue.map((item) => {
                    const meta = QUEUE_META[item.kind]
                    const rowContent = (
                      <>
                        <span
                          className={`flex size-8 flex-none items-center justify-center rounded-full ${meta.iconClass}`}
                        >
                          {meta.icon}
                        </span>
                        <div className="flex-1">
                          <p className="text-small font-bold">{item.title}</p>
                          <p className="mt-0.5 text-caption text-ink-muted">{item.detail}</p>
                        </div>
                        <StatusChip state="submitted" />
                      </>
                    )
                    return (
                      <li key={item.id} className="border-b border-line last:border-b-0">
                        <Link to={`/supervisor/approval-inbox?request=${item.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2">
                          {rowContent}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-5">
              <Panel
                title={`Coverage gaps · ${TODAY_SHORT_LABEL}`}
                trailing={
                  coverageGaps.length > 0 && (
                    <span className="rounded-full bg-error/10 px-2.5 py-0.5 text-caption font-bold text-error">
                      {coverageGaps.length}
                    </span>
                  )
                }
              >
                {coverageGaps.length === 0 ? (
                  <EmptyState>No coverage gaps today.</EmptyState>
                ) : (
                  <>
                    <p className="border-b border-line px-4 pt-2.5 pb-1.5 text-caption text-ink-muted">
                      {catalog.shift ? formatScheduleLabel(catalog.shift.time, format) : ''}
                    </p>
                    {coverageGaps.map((gap) => (
                      <PanelRow
                        key={gap.assignment}
                        title={gap.assignment}
                        detail={`${gap.actual} of ${gap.required} required`}
                      />
                    ))}
                  </>
                )}
              </Panel>

              <Panel
                title={`Overstaffed · ${TODAY_SHORT_LABEL}`}
                trailing={
                  overstaffedToday.length > 0 && (
                    <span className="rounded-full bg-error/10 px-2.5 py-0.5 text-caption font-bold text-error">
                      {overstaffedToday.length}
                    </span>
                  )
                }
              >
                {overstaffedToday.length === 0 ? (
                  <EmptyState>Nothing is overstaffed today.</EmptyState>
                ) : (
                  overstaffedToday.map((finding) => (
                    <PanelRow
                      key={finding.assignment}
                      title={finding.assignment}
                      detail={`${finding.actual} of ${finding.required} required, ${finding.actual - finding.required} over`}
                    />
                  ))
                )}
              </Panel>

              <Panel
                title="Detailed out"
                trailing={
                  <span className="rounded-full bg-surface-2 px-2.5 py-0.5 text-caption font-bold text-ink-subtle">
                    {detailedOut.length}
                  </span>
                }
              >
                {detailedOut.length === 0 ? (
                  <EmptyState>No one is detailed out.</EmptyState>
                ) : (
                  detailedOut.map((person) => (
                    <PanelRow key={person.title} title={person.title} detail={person.detail} />
                  ))
                )}
              </Panel>

              <Panel
                title="Planned overtime"
                trailing={
                  overtimeEntries.length > 0 && (
                    <span className="text-caption font-bold text-ink-muted">{overtimeTotal} hrs this period</span>
                  )
                }
              >
                {overtimeEntries.length === 0 ? (
                  <EmptyState>No overtime planned this period.</EmptyState>
                ) : (
                  overtimeEntries.map((entry, index) => (
                    <PanelRow key={`${entry.title}-${index}`} title={entry.title} detail={entry.detail} />
                  ))
                )}
              </Panel>

              <Panel title="Recently published">
                {published.length === 0 ? (
                  <EmptyState>Nothing published yet.</EmptyState>
                ) : (
                  published.map((item, index) => (
                    <div
                      key={`${item.title}-${index}`}
                      className="flex items-center gap-2.5 border-b border-line px-4 py-2.5 last:border-b-0"
                    >
                      <span className="flex size-7 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                        <FlagIcon />
                      </span>
                      <div>
                        <p className="text-small font-bold">{item.title}</p>
                        <p className="mt-0.5 text-caption text-ink-muted">{item.detail}</p>
                        {schedulingServiceEnabled() && item.acknowledgement && (
                          <p className="mt-0.5 text-caption text-ink-muted">
                            {item.acknowledgement.acknowledged} of {item.acknowledgement.total} acknowledged
                            {item.acknowledgement.waiting.length > 0 ? `. Waiting on ${item.acknowledgement.waiting.join(', ')}.` : '.'}
                          </p>
                        )}
                        {schedulingServiceEnabled() && item.acknowledgement && item.acknowledgement.waiting.length > 0 && item.version.latest && !item.version.ended && (
                          <button
                            type="button"
                            onClick={() => remind(item)}
                            disabled={reminding.has(`${item.version.periodStart}|${item.version.version}`)}
                            className="mt-1 flex min-h-11 items-center rounded-control px-1 text-small font-bold text-primary underline underline-offset-2 disabled:opacity-60"
                          >
                            Remind
                            <span className="sr-only"> {item.title}</span>
                          </button>
                        )}
                        {reminderNotes[`${item.version.periodStart}|${item.version.version}`] && (
                          <p role="status" className="mt-0.5 text-caption font-semibold text-ink-muted">
                            {reminderNotes[`${item.version.periodStart}|${item.version.version}`]}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </Panel>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
